import React, { useState } from 'react';
import { Button, Dropdown, Menu, notification } from 'antd';
import { ExportOutlined, DownOutlined } from '@ant-design/icons';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';

type Props = {
  style?: any;
  onExportAll: () => Promise<any>;
  onExportUsage: () => Promise<any>;
  onExportPricing: () => Promise<any>;
  disabled: boolean;
  hasUsageData: boolean;
  hasPricingData: boolean;
};

const ExportDropdown = ({ 
  style, 
  onExportAll, 
  onExportUsage, 
  onExportPricing, 
  disabled,
  hasUsageData,
  hasPricingData
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  
  console.log('ExportDropdown render:', { disabled, hasUsageData, hasPricingData, isLoading });
  
  const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = '.xlsx';

  const exportToExcel = async (data: any, fileName: string) => {
    try {
      let wb;
      
      // Handle object with multiple sheets
      if (typeof data === 'object' && !Array.isArray(data) && data !== null) {
        wb = XLSX.utils.book_new();
        const sheetNames = Object.keys(data);
        
        for (const sheetName of sheetNames) {
          const sheetData = data[sheetName];
          if (Array.isArray(sheetData) && sheetData.length > 0) {
            const ws = XLSX.utils.json_to_sheet(sheetData);
            XLSX.utils.book_append_sheet(wb, ws, sheetName);
          }
        }
      } else if (Array.isArray(data) && data.length > 0) {
        wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, 'Analytics');
      } else {
        throw new Error('No data to export');
      }
      
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(blob, fileName + fileExtension);
      
      notification.success({
        message: 'Export Successful',
        description: `${fileName} has been exported successfully`,
        placement: 'bottomLeft',
      });
    } catch (error) {
      console.error(error);
      notification.error({
        message: 'Export Failed',
        description: `${error}`,
        placement: 'bottomLeft',
      });
    }
  };

  const handleExport = async (exportType: 'all' | 'usage' | 'pricing') => {
    try {
      setIsLoading(true);
      let data;
      let fileName;
      
      switch (exportType) {
        case 'all':
          data = await onExportAll();
          fileName = 'analytics-complete';
          break;
        case 'usage':
          data = await onExportUsage();
          fileName = 'usage-analytics';
          break;
        case 'pricing':
          data = await onExportPricing();
          fileName = 'pricing-analytics';
          break;
      }
      
      if (!data || (Array.isArray(data) && data.length === 0)) {
        notification.warning({
          message: 'No Data to Export',
          description: 'No data available for the selected export type',
          placement: 'bottomLeft',
        });
        return;
      }
      
      await exportToExcel(data, fileName);
      
    } catch (error) {
      console.error('Export error:', error);
      notification.error({
        message: 'Export Failed',
        description: 'An error occurred while preparing the export',
        placement: 'bottomLeft',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMenuClick = ({ key }: any) => {
    console.log('Menu clicked:', key);
    handleExport(key as 'all' | 'usage' | 'pricing');
  };

  const menu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="all" disabled={!hasUsageData && !hasPricingData}>
        Export All Analytics
      </Menu.Item>
      <Menu.Item key="usage" disabled={!hasUsageData}>
        Export Usage Analytics Only
      </Menu.Item>
      <Menu.Item key="pricing" disabled={!hasPricingData}>
        Export Pricing Analytics Only
      </Menu.Item>
    </Menu>
  );

  if (disabled) {
    return (
      <Button 
        style={style} 
        icon={<ExportOutlined />} 
        type="primary" 
        disabled={true}
      >
        Export
      </Button>
    );
  }

  return (
    <Dropdown overlay={menu} placement="bottomLeft">
      <Button 
        style={style} 
        icon={<ExportOutlined />} 
        type="primary" 
        loading={isLoading}
      >
        Export <DownOutlined />
      </Button>
    </Dropdown>
  );
};

export default ExportDropdown;